/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactElement, FC, HTMLAttributes, useState } from 'react';
import { CalendarIcon } from '@radix-ui/react-icons';
import { cn } from 'src/domains/common/components/utils';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { observer } from 'src/utils/mobx-react';
import { ClickOutsideHandler } from 'src/domains/common/components/clickOutside';
import { FirstDayOfWeekType, CalendarState } from './CalendarState';
import { CalendarView } from './CalendarView';
import { TimezoneType } from 'src_common/utils/time/time';

export interface CalendarPropsType extends HTMLAttributes<HTMLDivElement> {
    label?: string;
    placeholder?: string;
    dataTest?: string;
    disabled?: boolean;
    formInputState: FormInputState<string, unknown>;
    description?: string;
    showTimePicker: boolean;
    firstDayOfWeek: FirstDayOfWeekType;
    errors: string[];
    timezone?: TimezoneType;
    allowTyping?: boolean;
    isCampaignCreation?: boolean;
}

/** @description form value will be saved as ISO-8601 string, e.g. YYYY-MM-DDTHH:mm:ss.SSS[Z] (2024-11-24T20:00:00.000Z) */
export const Calendar: FC<CalendarPropsType> = observer(
    'Calendar',
    ({
        formInputState,
        label,
        placeholder,
        description,
        dataTest,
        className,
        disabled,
        showTimePicker,
        firstDayOfWeek,
        errors,
        timezone,
        allowTyping,
        isCampaignCreation = false,
        ...props
    }): ReactElement => {
        const [calendarState] = useState(
            () => new CalendarState(formInputState, firstDayOfWeek, timezone, isCampaignCreation)
        );
        const {
            showCalendar,
            setShowCalendar,
            selectedDateString,
            typedDateString,
            setTypedDateString,
            applyTypedDateString,
        } = calendarState;

        const error = errors.length === 0 ? null : errors[0] ?? null;
        return (
            <ClickOutsideHandler onClickOutside={() => setShowCalendar(false)}>
                <div className='flex flex-col gap-2'>
                    <div
                        className='relative flex flex-col gap-2'
                        {...props}
                    >
                        {label !== undefined && <label className='text-sm'>{label}</label>}
                        <div
                            className='relative'
                            onClick={() => setShowCalendar(true)}
                        >
                            <input
                                data-test={dataTest}
                                disabled={disabled}
                                readOnly={allowTyping !== true}
                                value={typedDateString ?? selectedDateString}
                                onChange={(e) => {
                                    if (allowTyping === true) {
                                        setTypedDateString(e.target.value);
                                    }
                                }}
                                onBlur={() => {
                                    if (allowTyping === true) {
                                        applyTypedDateString();
                                    }
                                }}
                                placeholder={placeholder}
                                className={cn(
                                    'flex h-9 w-full rounded-md border border-solid border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400 disabled:cursor-not-allowed disabled:opacity-50 cursor-default',
                                    error === null ? '' : 'border-destructive',
                                    className
                                )}
                            />
                            <CalendarIcon
                                className={cn(
                                    'absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none',
                                    disabled === true && 'text-muted-foreground/50'
                                )}
                            />
                        </div>
                        {showCalendar && (
                            <CalendarView
                                calendar={calendarState}
                                showTimePicker={showTimePicker}
                            />
                        )}
                    </div>
                    {description !== undefined && error === null && (
                        <span className='text-sm text-muted-foreground'>{description}</span>
                    )}
                    {error === null ? null : <span className='text-sm text-destructive'>{error}</span>}
                </div>
            </ClickOutsideHandler>
        );
    }
);
